import React, { createRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { highlightPreformattedCode, resolveImportantNotice, wrapTablesInResponsiveDiv } from './utils/utils';

import layoutVariations from '../../../components/blocks/LayoutGroup/layoutVariations';
import { resolveResourceReferences } from './utils/anchorUtils';
import { normalizeHtml } from '../../../lib/Pipes/pipes';
import Bem from '../../../lib/bemUtils/bem';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { SONHTMLContent, SONResourceReferencesToUrls } from './types';

function getClass(className: string, groupLayout?: keyof typeof layoutVariations) {
  return groupLayout && groupLayout !== 'none' ? `${className} wysiwyg--${layoutVariations[groupLayout]}` : className;
}

function normalizeContent(content: string, resourceReferencesToToUrls: SONResourceReferencesToUrls, t: TFunction) {
  return normalizeHtml(
    content,
    resolveImportantNotice(t),
    resolveResourceReferences(resourceReferencesToToUrls, t),
    highlightPreformattedCode,
  );
}

type WysiwygProps = SONHTMLContent & {
  groupLayout?: keyof typeof layoutVariations;
  modifier?:
    | Modifier<'small-padding'>
    | Modifier<'very-small-padding'>
    | Modifier<'content-message'>
    | Modifier<'small-text'>
    | string[];
};

/**
 * We have to use dangerouslySetInnerHTML here, because the content comes as an
 * HTML from on2, and it comes as HTML string form TinyMCE
 */
export function Wysiwyg({ content, resourceReferencesToToUrls, modifier, groupLayout }: WysiwygProps) {
  const { t } = useTranslation();
  const wysiwygRef = createRef<HTMLDivElement>();

  const normalizedContent = normalizeContent(content, resourceReferencesToToUrls, t);

  useEffect(() => {
    wrapTablesInResponsiveDiv(wysiwygRef.current, t);
  }, [wysiwygRef, t]);

  if (!content || !normalizedContent) {
    return null;
  }

  return (
    <div className={getClass('wysiwyg-container', groupLayout)}>
      <div
        ref={wysiwygRef}
        className={Bem('wysiwyg', modifier)}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: normalizedContent }}
      />
    </div>
  );
}
